/**
 * ICU MessageFormat implementation of the {@link MessageFormatter} port
 * (Sprint-017.2 FR-4).
 *
 * Backed by `intl-messageformat` (FormatJS), the reference ICU MessageFormat
 * engine. It is pure JavaScript over ECMA-402 `Intl`, so it runs unchanged in
 * the browser, on the desktop and headless — no DOM, no node core module
 * (ADR-0006). Plural and select rules therefore come from CLDR data in the
 * runtime rather than from hand-written grammar rules in the UI.
 *
 * Compiled messages are cached per locale+message: compilation dominates the
 * cost of `tr()`, and the same handful of messages is re-rendered constantly.
 */
import IntlMessageFormat from 'intl-messageformat';
import { MessageFormatError } from './message-formatter.js';
/** FormatJS reports an absent parameter with this error code. */
const MISSING_VALUE_CODE = 'MISSING_VALUE';
export const DEFAULT_ICU_CACHE_LIMIT = 500;
export class IcuMessageFormatter {
    compiled = new Map();
    cacheLimit;
    constructor(options = {}) {
        this.cacheLimit = options.cacheLimit ?? DEFAULT_ICU_CACHE_LIMIT;
    }
    format(message, params, context) {
        const formatter = this.compile(message, context);
        try {
            // ICU can yield rich-text parts; translations are plain strings, so any
            // non-string part is flattened rather than surfaced to the UI.
            const formatted = formatter.format(params);
            return Array.isArray(formatted) ? formatted.join('') : String(formatted);
        }
        catch (error) {
            if (isMissingValueError(error)) {
                throw new MessageFormatError('missing-parameter', `Message for "${context.key}" requires a parameter that was not supplied: ${messageOf(error)}`, error);
            }
            throw new MessageFormatError('invalid-message', `Message for "${context.key}" could not be formatted: ${messageOf(error)}`, error);
        }
    }
    /** Number of compiled messages currently cached. */
    get cacheSize() {
        return this.compiled.size;
    }
    clearCache() {
        this.compiled.clear();
    }
    compile(message, context) {
        const cacheKey = `${context.locale} ${message}`;
        const cached = this.compiled.get(cacheKey);
        if (cached !== undefined) {
            return cached;
        }
        let formatter;
        try {
            formatter = new IntlMessageFormat(message, context.locale);
        }
        catch (error) {
            throw new MessageFormatError('invalid-message', `Message for "${context.key}" is not valid ICU MessageFormat: ${messageOf(error)}`, error);
        }
        if (this.compiled.size >= this.cacheLimit) {
            const oldest = this.compiled.keys().next();
            if (!oldest.done) {
                this.compiled.delete(oldest.value);
            }
        }
        this.compiled.set(cacheKey, formatter);
        return formatter;
    }
}
function isMissingValueError(error) {
    return (typeof error === 'object' &&
        error !== null &&
        error.code === MISSING_VALUE_CODE);
}
function messageOf(error) {
    return error instanceof Error ? error.message : String(error);
}
//# sourceMappingURL=icu-message-formatter.js.map